import { Component, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from '../../../auth/auth.service';
import { UserDto } from '../../../auth/user.dto';
import { Title } from '../../../shared/models/enums/title.enum';
import { Pagination } from '../../../shared/pagination/pagination';
import { PaginationOptions } from '../../../shared/pagination/pagination-options';
import { BeneficiariesService } from '../../../shared/services/beneficiaries.service';
import { SortDirection, SortEvent } from '../../../shared/sortable/sort-event';
import { SortableDirective } from '../../../shared/sortable/sortable.directive';
import { ToastService } from '../../../shared/toast/toast.service';
import { BeneficiaryBaseComponent } from '../../beneficiary-base.component';
import { BeneficiaryDto } from '../../dto/beneficiary.dto';
import { AssessmentDetailsComponent } from './assessment-details/assessment-details.component';
import { AssessmentDto } from './assessment-details/assessment.dto';
import { AssessmentContext, assessmentContextLabels, AssessmentStatus, assessmentStatusLabels } from './assessment.enums';
import { AssessmentService } from './assessment.service';
import { UserService } from '../../../shared/services/user.service';

@Component({
  selector: 'app-assessment',
  templateUrl: './assessment.component.html',
})
export class AssessmentComponent extends BeneficiaryBaseComponent implements OnInit, OnDestroy {
  public user: UserDto;
  public readonly assessmentStatusMap: Map<AssessmentStatus, string> = assessmentStatusLabels;
  public readonly assessmentContextMap: Map<AssessmentContext, string> = assessmentContextLabels;
  private static readonly resetAssessments: Pagination<AssessmentDto> = new Pagination([], 0, 0, 1, new PaginationOptions(1, 25));

  constructor(
    private readonly modalService: NgbModal,
    private readonly authService: AuthService,
    private readonly toastService: ToastService,
    private readonly assessmentService: AssessmentService,
    private readonly beneficiariesService: BeneficiariesService,
    private readonly userService: UserService,
  ) {
    super();
  }

  @ViewChildren(SortableDirective) headers: QueryList<SortableDirective>;
  public assessments: Pagination<AssessmentDto> = AssessmentComponent.resetAssessments;

  public async ngOnInit(): Promise<void> {
    this.user = await this.authService.getUser();

    this.beneficiariesService.beneficiary.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((beneficiary: BeneficiaryDto) => (this.beneficiary = beneficiary));

    await this.updateAssessments(this.assessments.options);

    this.assessmentService.assessmentCreated$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.reloadAssessments();
    });
  }

  private async reloadAssessments(): Promise<void> {
    this.assessments = await this.assessmentService.findByBeneficiaryId(this.beneficiary.id, this.assessments.options);
  }

  private async updateAssessments(pagination: PaginationOptions): Promise<void> {
    this.assessments = await this.assessmentService.findByBeneficiaryId(this.beneficiary.id, pagination);
  }

  private async openModal(assessmentId?: number): Promise<void> {
    const modal: NgbModalRef = this.modalService.open(AssessmentDetailsComponent, {
      windowClass: 'rightModal',
      size: 'xl',
      backdrop: 'static',
    });
    const modalComponent: AssessmentDetailsComponent = modal.componentInstance as AssessmentDetailsComponent;
    const assessment = assessmentId ? await this.assessmentService.findById(assessmentId) : new AssessmentDto();
    await modalComponent.onInit(this.beneficiary.id, assessment);
  }

  public async openCreateModal(): Promise<void> {
    if (!(await this.userService.getSignature())) {
      this.toastService.show('Veuillez enregistrer une signature dans votre profil', 'danger');
      return;
    }
    if (!this.user?.groups?.some((group: string) => ['ADMIN', 'STRUCTURE', 'TERRITORY', 'DIRECTION+'].includes(group))) {
      this.toastService.show("Vous n'avez pas l'autorisation d'accéder à cette page.", 'danger');
      return;
    }

    try {
      this.openModal();
    } catch (error) {
      // No log
    }
  }

  public async openConsultModal(assessmentId: number): Promise<void> {
    if (this.user?.groups?.some((group: string) => ['ADMIN', 'STRUCTURE', 'DIRECTION', 'DIRECTION+', 'TERRITORY'].includes(group))) {
      try {
        if (!this.beneficiary.title || this.beneficiary.title === ('Unknown' as Title)) {
          throw new Error('Unknown title.');
        }
        this.openModal(assessmentId);
      } catch (error) {
        if (error.status === 461) {
          this.toastService.show('Veuillez compléter les informations obligatoires des onglets Complément et Emploi.', 'danger');
        } else if (error.message === 'Unknown title.') {
          this.toastService.show('Veuillez sélectionner une civilité différente de « Inconnu » pour le bénéficiaire.', 'danger');
        }
      }
    } else {
      this.toastService.show("Vous n'avez pas l'autorisation d'accéder à cette page.", 'danger');
    }
  }

  public async onSort(event: SortEvent): Promise<void> {
    // resetting other headers
    const column: string = event.column;
    const direction: SortDirection = event.direction;
    this.headers.forEach((header: SortableDirective) => {
      if (header.sortable !== column) {
        header.direction = '';
      }
    });
    await this.updateAssessments(this.assessments.options.withSort(column, direction == 'desc'));
  }

  public async changePage(pageNb?: number): Promise<void> {
    if (pageNb) {
      await this.updateAssessments(this.assessments.options.withPage(pageNb));
    }
  }

  public async changeLimit(event: any): Promise<void> {
    if (event?.target?.value) {
      const limit: number = event.target.value;
      await this.updateAssessments(this.assessments.options.withLimit(limit));
    }
  }
}
